import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Bell, Check } from "@phosphor-icons/react";
import { timeAgo } from "@/lib/format";

const SEVERITY_COLOR = {
  info: "#8B95A5",
  warning: "#FFB300",
  critical: "#FF3B30",
  success: "#00E676",
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = async () => {
    try {
      const [n, c] = await Promise.all([
        api.get("/notifications", { params: { limit: 30 } }),
        api.get("/notifications/unread-count"),
      ]);
      setItems(n.data);
      setUnread(c.data.count);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 12000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        className="relative h-9 w-9 flex items-center justify-center rounded-sm border border-[#272A30] hover:border-[#00E5FF] hover:text-[#00E5FF] text-[#8B95A5]"
        onClick={() => setOpen((v) => !v)}
        data-testid="notif-bell-btn"
      >
        <Bell size={16} weight="bold" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 bg-[#00E5FF] text-[#0A0D14] text-[9px] font-bold px-1 py-0.5 leading-none rounded-sm" data-testid="notif-unread-count">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-[380px] panel z-30" data-testid="notif-panel">
          <div className="panel-header">
            <span>Activity Feed</span>
            <button onClick={markAll} className="text-[10px] hover:text-white flex items-center gap-1" data-testid="notif-mark-all">
              <Check size={12} /> Mark all read
            </button>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-6 text-xs text-[#4B5563] text-center">No notifications yet.</div>
            ) : items.map((n) => (
              <div key={n.id} className={`px-4 py-3 border-b border-[#272A30] ${n.read ? "opacity-60" : ""}`} data-testid={`notif-item-${n.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: SEVERITY_COLOR[n.severity] || "#8B95A5" }} />
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#8B95A5]">{n.type}</span>
                  <span className="text-[10px] text-[#4B5563] ml-auto font-mono">{timeAgo(n.created_at)}</span>
                </div>
                <div className="text-sm">{n.title}</div>
                <div className="text-xs text-[#8B95A5] mt-1">{n.message}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
